// Kairos the Archivist. Pure. No React, no Three.js.
//
// ── Codex (Kairos) ──
//   "Genesis → Archive the state of the realm.
//    Deathrite → Return all archived cards to their recorded state.
//    Then banish Kairos."
//
// The archive is a FULL snapshot of every in-play card: identity,
// location, tap/flip state, both power halves, manual statuses,
// subsurface region, controller side, and carried equipment. Nothing
// is left out. "recorded state" means all of it.
//
// Restore semantics (confirmed 2026-06-12):
//   • Archived card still in play → rewound to its record (position,
//     tap, flip, stats, statuses, subsurface, controller, equipment).
//   • Archived card that left play → returns to the realm at its
//     recorded cell with its recorded state, pulled from WHEREVER it
//     ended up: any pile (cemetery, spellbook, atlas, collection) or
//     a locally-visible hand. "All archived cards" names the specific
//     physical copy. It is tracked across zone hops (died → bounced
//     to hand → discarded again, etc.).
//   • EXCEPT banished (FAQ): "banished cards can never be returned;
//     sometimes knowledge is truly lost." Tokens are the exception to
//     the exception. Unlimited copies exist, so an archived token
//     returns from banished and is recreated from the record if its
//     row vanished entirely.
//   • The restore NEVER fabricates a duplicate of a real card: one
//     found in no visible zone (the opponent's private hand in
//     multiplayer) produces no step rather than a respawned copy.
//   • A returning SITE reclaims its location: whatever site sits at
//     the recorded cell now (the Rubble that replaced it, or any
//     post-archive site) is BANISHED (FAQ: "the site that's in the
//     way gets banished!"), since one location holds one site. The
//     eviction is displacement, not destruction. No Rubble spawns.
//   • Cards that entered play after the archive are otherwise
//     untouched.

// Classify a pile as a departure zone the restore can pull from.
// Pile NAMES carry player suffixes ("Cemetery P1", "Banished P2".
// see findOrCreateCemetery / findOrCreateBanished), so the match is
// a prefix on BOTH id and name. An exact-name comparison here once
// made every cemetery invisible to the restore. The plan came back
// empty no matter what died. Keep this the single classification
// point; the test pins the real pile shapes.
export function departedPileKind(pile) {
  const id = String(pile?.id || '');
  const name = String(pile?.name || '');
  if (id.startsWith('cemetery') || name.startsWith('Cemetery')) return 'cemetery';
  if (id.startsWith('banished') || name.startsWith('Banished')) return 'banished';
  return null;
}

/**
 * Every row in any visible departure zone (except `excludeId`) as a
 * departed entry. EVERY pile qualifies. A card the archive tracked
 * can be pulled back from the cemetery, banished, a deck, or the
 * collection; zone hops after death don't break the chain. `hands`
 * ([{ key, cards }]) covers the locally-visible hand arrays; entries
 * from there carry `handKey` instead of `pileId` so the resolver
 * removes through the hand-state primitive.
 */
export function collectDepartedRows(piles, excludeId = null, hands = []) {
  const out = [];
  for (const pile of piles || []) {
    const kind = departedPileKind(pile)
      || String(pile?.name || 'pile').split(' ')[0].toLowerCase();
    for (const row of pile.cards || []) {
      if (row.id === excludeId) continue;
      out.push({ pile: kind, card: row, pileId: pile.id });
    }
  }
  for (const hand of hands || []) {
    for (const row of hand.cards || []) {
      if (row.id === excludeId) continue;
      out.push({ pile: 'hand', card: row, handKey: hand.key });
    }
  }
  return out;
}

export function snapshotRealm(boardCards) {
  return (boardCards || []).map((c) => ({
    id: c.id,
    cardId: c.cardId,
    name: c.name,
    imageUrl: c.imageUrl ?? null,
    printingId: c.printingId ?? null,
    foiling: c.foiling ?? null,
    type: c.type ?? null,
    isSite: !!c.isSite,
    isToken: !!c.isToken,
    cell: c.cell ? { ...c.cell } : null,
    tapped: !!c.tapped,
    faceDown: !!c.faceDown,
    rotated: !!c.rotated,
    currentAttack: c.currentAttack ?? null,
    currentLife: c.currentLife ?? null,
    statuses: [...(c.statuses || [])],
    subsurface: c.subsurface ?? null,
    movementBonus: c._movementBonus || 0,
    counters: c._counters || 0,
    equippedCards: (c.equippedCards || []).map((eq) => ({ ...eq })),
  }));
}

// Field-by-field "does the live card match its record" check. Cell is
// compared separately by the caller (it lives on the enriched live
// row, not the raw instance).
export function recordMatchesLive(rec, cur) {
  if (!cur) return false;
  if (cur.cardId !== rec.cardId) return false;
  if (!!cur.tapped !== rec.tapped) return false;
  if (!!cur.faceDown !== rec.faceDown) return false;
  if (!!cur.rotated !== rec.rotated) return false;
  if ((cur.currentAttack ?? null) !== rec.currentAttack) return false;
  if ((cur.currentLife ?? null) !== rec.currentLife) return false;
  if ((cur.subsurface ?? null) !== rec.subsurface) return false;
  if ((cur._movementBonus || 0) !== rec.movementBonus) return false;
  if ((cur._counters || 0) !== rec.counters) return false;
  const curStatuses = [...(cur.statuses || [])].sort();
  const recStatuses = [...rec.statuses].sort();
  if (curStatuses.length !== recStatuses.length
      || curStatuses.some((s, i) => s !== recStatuses[i])) return false;
  const curEq = (cur.equippedCards || []).map((e) => e.id).sort();
  const recEq = rec.equippedCards.map((e) => e.id).sort();
  if (curEq.length !== recEq.length || curEq.some((id, i) => id !== recEq[i])) return false;
  if (rec.cell && (cur.cell?.col !== rec.cell.col || cur.cell?.row !== rec.cell.row)) return false;
  return true;
}

/**
 * Diff the archive against the live board.
 *
 * @param snapshot   output of snapshotRealm
 * @param boardCards live in-play instances, enriched with `.cell`
 * @param departed   collectDepartedRows output. Every visible zone
 *                   row ({ pile, card, pileId } or hand entries with
 *                   handKey) for archived cards that left play
 * @returns ordered plan steps:
 *   { kind: 'restore_state', id, rec }. In-play rewind
 *   { kind: 'replace', id, rec }. In-play card whose
 *       identity mutated; remove it and respawn the record
 *   { kind: 'clear_site_at', cell, sourceName }. Delete the post-
 *       archive site occupying a returning site's cell
 *   { kind: 'return_to_play', rec, fromPile, pileId, handKey? }
 */
export function buildRestorePlan(snapshot, boardCards, departed) {
  const live = new Map((boardCards || []).map((c) => [c.id, c]));
  const gone = new Map((departed || []).map((d) => [d.card.id, d]));
  const archivedIds = new Set((snapshot || []).map((r) => r.id));
  const plan = [];

  // A site re-entering (or being replaced at) its recorded cell
  // evicts whatever site holds that cell now. Unless that occupant
  // is itself an archived site already standing where it belongs.
  const clearCellForSite = (rec) => {
    if (!rec.isSite || !rec.cell) return;
    const occupant = (boardCards || []).find((c) =>
      c.isSite
      && c.id !== rec.id
      && c.cell
      && c.cell.col === rec.cell.col
      && c.cell.row === rec.cell.row
      && !archivedIds.has(c.id));
    if (occupant) plan.push({ kind: 'clear_site_at', cell: { ...rec.cell }, occupantId: occupant.id });
  };

  // Sites first, then everything else. Execution is sequential but
  // the card text says all changes happen simultaneously. A unit
  // rewound onto a cell whose site hasn't returned yet would read as
  // standing in the void. Landing the sites first makes the
  // sequential pass observationally simultaneous.
  const ordered = [
    ...(snapshot || []).filter((r) => r.isSite),
    ...(snapshot || []).filter((r) => !r.isSite),
  ];
  for (const rec of ordered) {
    const cur = live.get(rec.id);
    if (cur) {
      if (cur.cardId !== rec.cardId) {
        // Identity mutated in place. Rebuild from the record.
        clearCellForSite(rec);
        plan.push({ kind: 'replace', id: rec.id, rec });
        continue;
      }
      if (!recordMatchesLive(rec, cur)) {
        plan.push({ kind: 'restore_state', id: rec.id, rec });
      }
      continue;
    }
    const dep = gone.get(rec.id);
    // FAQ: "banished cards can never be returned; sometimes knowledge
    // is truly lost." Banished is a legal source only for tokens
    // ("there are always unlimited copies of tokens").
    if (dep && !(dep.pile === 'banished' && !rec.isToken)) {
      clearCellForSite(rec);
      const step = { kind: 'return_to_play', rec, fromPile: dep.pile, pileId: dep.pileId };
      if (dep.handKey) step.handKey = dep.handKey;
      plan.push(step);
    } else if (!dep && rec.isToken) {
      // Archived token whose row vanished entirely. Recreate it from
      // the record. The no-copy rule is about real cards; tokens are
      // an unlimited supply by definition.
      clearCellForSite(rec);
      plan.push({ kind: 'return_to_play', rec, fromPile: 'token' });
    }
  }
  return plan;
}
